import { useEffect, useMemo } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import qs from 'qs'

import { Filter } from 'cat-shared/utils'
import { DEFAULT_LANG, DEFAULT_THEME, MIN_REQ_TIME_MS, PAGES_CONFIG } from 'client/utils'
import { useAuthenticateUser } from 'client/api'
import { AppLoader, AppDimmer, AppUiPreview } from 'client/ui-building-blocks'
import { DataManager } from 'client/data-manager'
import { NoAccessModal } from 'client/components'

import {
  useAppContext,
  usePageStateContext,
  useModalDialogContext,
  usePage,
} from 'client/hooks'

import {
  AuthPage,
  ProfilePage,
  SettingsPage,
  QuestionPage,
  HomePage,
  BrowseQuestionsPage,
  RandomQuestionPage,
  CreateQuestionPage,
  AboutPage,
  BestOfTheBestPage,
} from 'client/pages'

import {
  displaySnackBar,
  getLocalAuthUser,
  getLocalLastUserConfig,
  startUserSocket,
  stopQuestionSocket,
  killRecoverEmailSocket,
} from 'client/services'

import TopBar from './TopBar/top-bar'
import Footer from './Footer/footer'
import AuthInfo from './AuthInfo/auth-info'
import AccountActivationMsg from './AccountActivationMsg/account-activation-msg'
import PageContainer from './PageContainer/page-container'

import styles from './root-page.module.scss'

const PATHS = Object.keys(PAGES_CONFIG)

const RootPage = () => {
  const { t, i18n } = useTranslation()
  const { pathname, search } = useLocation()
  const { navigateTo, checkQueryParams } = usePage()

  const { isAppLoading, setIsAppLoading, recoverPassToken, setAuthUser, authUser } =
    useAppContext()

  const { setBrowseQuestionsPageState, setListActiveQuestionId } = usePageStateContext()
  const { showQuestionModal, toggleQuestionModal } = useModalDialogContext()

  const { mutateAsync: authenticate } = useAuthenticateUser({
    authType: 'TOKEN',
    onSuccess: data => {
      const lastUserConfig = getLocalLastUserConfig()
      const html = document.querySelector('html')
      html.setAttribute('data-theme', lastUserConfig?.theme || DEFAULT_THEME)
      i18n.changeLanguage(lastUserConfig?.lang || DEFAULT_LANG)

      if (data.user.config.confirmed) {
        setBrowseQuestionsPageState(state => ({ ...state, filter: Filter.Answered }))
      }

      setAuthUser(data.user)

      if (['/', '/auth'].includes(pathname) || !PATHS.includes(pathname)) {
        navigateTo({ path: '/home' })
      }
    },
    onError: err => {
      navigateTo({ path: '/auth' })
      if (err?.msg) {
        displaySnackBar(err.msg)
      }
    },
  })

  useEffect(() => {
    const token = getLocalAuthUser().token

    if (!token) {
      const lastUserConfig = getLocalLastUserConfig()
      i18n.changeLanguage(lastUserConfig?.lang || DEFAULT_LANG)

      if (
        ['/', '/profile', '/settings'].includes(pathname) ||
        !PATHS.includes(pathname)
      ) {
        setTimeout(() => {
          setIsAppLoading(false)
          navigateTo({ path: '/home' })
        }, MIN_REQ_TIME_MS)
      } else {
        setTimeout(() => {
          setIsAppLoading(false)
        }, MIN_REQ_TIME_MS)
      }
    } else {
      authenticate({
        token,
      }).then(
        data => {
          startUserSocket(data.user._id.toString())
          setIsAppLoading(false)
        },
        () => {
          setIsAppLoading(false)
        }
      )
    }
  }, [])

  useEffect(() => {
    if (!recoverPassToken) {
      killRecoverEmailSocket()
    }
  }, [recoverPassToken])

  useEffect(() => {
    if (!['/home', '/browse', '/question'].includes(pathname) || !showQuestionModal) {
      stopQuestionSocket()
    }
  }, [pathname, showQuestionModal])

  useEffect(() => {
    document.title = (PAGES_CONFIG[pathname] || PAGES_CONFIG['/']).title
  }, [pathname])

  useEffect(() => {
    const { questionId } = qs.parse(search.substring(1))
    if (questionId) {
      navigateTo({ path: pathname })
    }
  }, [pathname])

  useEffect(() => {
    const valid = checkQueryParams(pathname, search)
    if (valid) {
      const { _id, questionId } = qs.parse(search.substring(1))
      if (['/home', '/browse'].includes(pathname)) {
        if (questionId) {
          setListActiveQuestionId(questionId as string)
        } else {
          setListActiveQuestionId(null)
        }
      } else if (pathname === '/create') {
        toggleQuestionModal(Boolean(_id))
      }
    }
  }, [search])

  return useMemo(() => {
    if (isAppLoading) {
      return <AppLoader absolute />
    }

    if (['/', '/auth'].includes(pathname) && authUser) {
      navigateTo({ path: '/home', replace: true })
      return null
    } else if (['/', '/profile', '/settings'].includes(pathname) && !authUser) {
      navigateTo({ path: '/auth', replace: true })
      return null
    }

    const localAuthUser = getLocalAuthUser?.()

    const displayDataManager =
      localAuthUser && localAuthUser.token && Boolean(localAuthUser.isAdmin)

    const displayHeader = pathname !== '/ui'
    const displayFooter = pathname !== '/ui' && pathname !== '/about'

    return (
      <>
        <AppDimmer />
        <NoAccessModal />
        <div className={styles.rootPage}>
          {displayHeader ? (
            <div>
              <TopBar />
            </div>
          ) : null}
          <div className={styles.mainContent}>
            <AuthInfo />
            <PageContainer>
              <AccountActivationMsg />
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/question" element={<QuestionPage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/browse" element={<BrowseQuestionsPage />} />
                <Route path="/random" element={<RandomQuestionPage />} />
                <Route path="/create" element={<CreateQuestionPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/best" element={<BestOfTheBestPage />} />
                {displayDataManager ? (
                  <Route path="/data" element={<DataManager />} />
                ) : null}
                {process.env.NODE_ENV === 'development' ? (
                  <Route path="/ui" element={<AppUiPreview />} />
                ) : null}
                <Route path="/" element={null} />
              </Routes>
            </PageContainer>
          </div>
          {displayFooter ? <Footer /> : null}
        </div>
      </>
    )
  }, [pathname, isAppLoading, t])
}

export default RootPage
